import React, { useCallback, useMemo, useState } from 'react';
import {
    AllEntitiesDropdown,
    AutoGrid,
    Button,
    ControlledFormInput,
    DateField,
    DialogV2,
    EntityAutocomplete,
    FlashMessage,
    FormWrapper,
    TextField,
} from 'c-components';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import apiClient from 'c-data/apiClient';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Translate, useCommonTranslation } from 'c-translation';
import to from 'await-to-js';
import { isValid } from 'date-fns';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import type { VirtualCampaignBuilderSummaryPayload } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import type { FormWrapperProps } from 'c-components/Forms/FormWrapper/FormWrapper';
import { useFormContext } from 'react-hook-form';
import VirtualCampaignSummary from './VirtualCampaignSummary';
import VirtualCampaignBuilderPlaybackSummary from './VirtualCampaignBuilderPlaybackSummary';
import type { VirtualCampaignFormValues } from './virtualCampaignPayload';
import {
    buildVirtualCampaignPayload,
    playbackSummaryName,
    virtualCampaignDatesInvalid,
} from './virtualCampaignPayload';

type FormStructure = VirtualCampaignFormValues;

const marketPerms = [PermissionName.UniledportalMarketRead];
const ownerPerms = [PermissionName.UniledportalMarketRead];
const mediaPerms = [PermissionName.Pop_displaysMediaRead];

const VirtualCampaignBuilderForm: React.FC<{ isLoading }> = ({ isLoading }) => {
    const t = useCommonTranslation();
    const { formState, watch } = useFormContext();

    const startDate = watch('start_date');
    const endDate = watch('end_date');
    const { parseApiDayMonthYearFormat } = useDateUtils();

    const minDate = useMemo(() => {
        const parsed = parseApiDayMonthYearFormat(startDate);
        if (isValid(parsed)) return parsed;

        return undefined;
    }, [startDate, parseApiDayMonthYearFormat]);

    const dateError = useMemo(
        () =>
            virtualCampaignDatesInvalid(
                parseApiDayMonthYearFormat(startDate),
                parseApiDayMonthYearFormat(endDate),
            ),
        [startDate, endDate, parseApiDayMonthYearFormat],
    );

    return (
        <Box px={2}>
            <AutoGrid spacing={2} xs={12}>
                <AutoGrid gap={2}>
                    <ControlledFormInput
                        name="start_date"
                        render={({ field }) => (
                            <DateField
                                {...field}
                                openOnFocus
                                inputProps={{
                                    variant: 'standard',
                                    fullWidth: false,
                                    label: t(
                                        'Modules.PopDisplays.VirtualCampaignBuilder.form.startDateLabel',
                                    ),
                                }}
                            />
                        )}
                    />
                    <ControlledFormInput
                        name="end_date"
                        render={({ field }) => (
                            <DateField
                                {...field}
                                openOnFocus
                                minDate={minDate}
                                inputProps={{
                                    variant: 'standard',
                                    fullWidth: false,
                                    label: t(
                                        'Modules.PopDisplays.VirtualCampaignBuilder.form.endDateLabel',
                                    ),
                                }}
                            />
                        )}
                    />
                </AutoGrid>

                <IfHasAllPermissions permissions={marketPerms}>
                    <ControlledFormInput
                        name="market_id"
                        render={({ field }) => (
                            <AllEntitiesDropdown
                                {...field}
                                label={t(
                                    'Modules.PopDisplays.VirtualCampaignBuilder.form.marketLabel',
                                )}
                                useLocalEntityData
                                multi={false}
                                entityName="Market"
                                labelField="name"
                            />
                        )}
                    />
                </IfHasAllPermissions>

                <IfHasAllPermissions permissions={ownerPerms}>
                    <ControlledFormInput
                        name="owner_ids"
                        render={({ field }) => (
                            <EntityAutocomplete
                                {...field}
                                multiple
                                fullWidth
                                textFieldProps={{
                                    label: t(
                                        'Modules.PopDisplays.VirtualCampaignBuilder.form.ownerLabel',
                                    ),
                                }}
                                entityName="Displays_Owner"
                                searchColumns="name"
                                labelColumn="name"
                            />
                        )}
                    />
                </IfHasAllPermissions>

                <IfHasAllPermissions permissions={mediaPerms}>
                    <ControlledFormInput
                        name="media_item_names"
                        render={({ field }) => (
                            <EntityAutocomplete
                                {...field}
                                multiple
                                fullWidth
                                entityName="PopDisplays_Media"
                                textFieldProps={{
                                    label: t(
                                        'Modules.PopDisplays.VirtualCampaignBuilder.form.mediaItemsTextarea',
                                    ),
                                }}
                                searchColumns="name"
                                labelColumn="name"
                                valueColumn="name"
                            />
                        )}
                    />
                </IfHasAllPermissions>

                <ControlledFormInput
                    name="media_item_text"
                    render={({ field }) => (
                        <TextField
                            {...field}
                            variant="outlined"
                            label={t(
                                'Modules.PopDisplays.VirtualCampaignBuilder.form.mediaItemsTextarea',
                            )}
                            multiline
                            minRows={5}
                            inputProps={{ sx: { resize: 'both' } }}
                        />
                    )}
                />
                <Button type="submit" disabled={!formState.isValid || isLoading || dateError}>
                    <Translate path="Modules.PopDisplays.VirtualCampaignBuilder.form.submitBtnLabel" />
                </Button>
            </AutoGrid>
        </Box>
    );
};

const VirtualCampaignBuilderFormWrapper: React.FC = () => {
    const [submittedPayload, setSubmittedPayload] =
        useState<VirtualCampaignBuilderSummaryPayload>(null);
    const { data, start, isLoading, hasFailed, error } = useAPIClientRequest(
        apiClient.VirtualCampaignBuilder.summary,
    );
    const summaries = useMemo(
        () => (Array.isArray(data?.data?.data) ? data?.data?.data : null),
        [data],
    );
    const onSubmit = useCallback(
        async (data: FormStructure) => {
            const payload = buildVirtualCampaignPayload(data);
            setSubmittedPayload(payload);
            await to(start(payload));
        },
        [start],
    );
    const opts = useMemo<FormWrapperProps<FormStructure>['formOptions']>(
        () => ({
            defaultValues: {
                media_item_names: [],
                media_item_text: '',
                owner_ids: [],
                media_items: [],
                market_id: null,
                title: '',
            },
        }),
        [],
    );

    const {
        data: StandardData,
        start: StartDataExport,
        isLoading: IsDataExportLoading,
        hasFailed: HasDataExportFailed,
        hasSucceeded: HasDataExportSucceeded,
        error: DataExportError,
        reset: ResetDataExport,
    } = useAPIClientRequest(apiClient.VirtualCampaignBuilder.standardPlaysExport);
    const generateStandardReport = useCallback(async () => {
        const [err, res] = await to(StartDataExport(submittedPayload));

        // A failed request is shown in the error dialog below (HasDataExportFailed).
        if (err || !res) {
            return;
        }

        // If response is a CSV string:
        const csvContent = res.data;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', 'standard_plays_export.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [submittedPayload, StartDataExport]);
    const [rowIdSummary, setRowIdSummary] = useState<number | null>(null);
    const [displayIdSummary, setDisplayIdSummary] = useState<number>(null);
    const onCloseSummary = useCallback(() => {
        setDisplayIdSummary(null);
        setRowIdSummary(null);
    }, []);

    const openSummary = useMemo(
        () =>
            rowIdSummary != null
                ? summaries?.find(sum => sum.channel_frame_id === rowIdSummary)
                : null,
        [rowIdSummary, summaries],
    );
    const onSummaryOpen = useCallback((row, display) => {
        setRowIdSummary(row);
        setDisplayIdSummary(display);
    }, []);
    const t = useCommonTranslation();

    const openSummaryName = useMemo(
        () => playbackSummaryName(openSummary, displayIdSummary),
        [openSummary, displayIdSummary],
    );

    return (
        <>
            <DialogV2
                onClose={onCloseSummary}
                open={openSummary != null}
                maxWidth="xl"
                title={
                    <Typography variant="h4">
                        {t(
                            'Modules.PopDisplays.VirtualCampaignBuilder.summary.table.playbackSummaryDialogTitle',
                            { name: openSummaryName },
                        )}
                    </Typography>
                }
            >
                {openSummary && (
                    <VirtualCampaignBuilderPlaybackSummary
                        summaryPayload={submittedPayload}
                        summary={openSummary}
                        id={rowIdSummary}
                    />
                )}
            </DialogV2>
            <FormWrapper<FormStructure> onSubmit={onSubmit} formOptions={opts}>
                <Stack gap={2}>
                    <VirtualCampaignBuilderForm isLoading={isLoading} />
                    {hasFailed && <FlashMessage status="error">{String(error)}</FlashMessage>}
                    {summaries && (
                        <>
                            <Stack flexDirection="row" gap={2} justifyContent="flex-end">
                                {/* standard report export */}
                                <DialogV2 onClose={ResetDataExport} open={HasDataExportSucceeded}>
                                    <FlashMessage status="success">
                                        {t(
                                            'Modules.PopDisplays.VirtualCampaignBuilder.summary.exportRerportSuccess',
                                        )}
                                    </FlashMessage>
                                </DialogV2>
                                <DialogV2 onClose={ResetDataExport} open={HasDataExportFailed}>
                                    <FlashMessage status="error">
                                        {String(DataExportError)}
                                    </FlashMessage>
                                </DialogV2>
                                {IsDataExportLoading ? (
                                    <CircularProgress />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={generateStandardReport}
                                        disabled={IsDataExportLoading}
                                    >
                                        {t(
                                            'Modules.PopDisplays.VirtualCampaignBuilder.summary.standardReport',
                                        )}
                                    </Button>
                                )}

                                {/* Displays report export */}
                                {/* <Button */}
                                {/*    variant="outlined" */}
                                {/*    onClick={generateDisplayReport} */}
                                {/*    disabled={IsDisplayDataExportLoading} */}
                                {/* > */}
                                {/*    {t( */}
                                {/*        'Modules.PopDisplays.VirtualCampaignBuilder.summary.displaysReport', */}
                                {/*    )} */}
                                {/* </Button> */}
                            </Stack>
                            <VirtualCampaignSummary
                                summaries={summaries}
                                onSummaryOpen={onSummaryOpen}
                            />
                        </>
                    )}
                </Stack>
            </FormWrapper>
        </>
    );
};

export default VirtualCampaignBuilderFormWrapper;
